"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useMemo, useCallback } from "react";

import { useLocale } from "next-intl";

import {
  buildPageUrl,
  buildLimitUrl,
  extractPageFromPath,
} from "../utils/url/pagination";

interface UseListingPaginationOptions {
  basePath: string;
  defaultLimit?: number;
}

interface ListingPaginationReturn {
  currentPage: number;
  limit: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

/**
 * Unified listing pagination hook for Buy/Developers/Rent pages
 * Handles both path-based pagination (/page/N) and URL search parameters
 *
 * @param options - Configuration options
 * @returns Pagination state and handlers
 */
export function useListingPagination({
  basePath,
  defaultLimit = parseInt(process.env.NEXT_PUBLIC_PAGE_SIZE || "12", 10),
}: UseListingPaginationOptions): ListingPaginationReturn {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locale = useLocale();

  // Extract current page, preferring explicit ?page query when provided
  const currentPage = useMemo(() => {
    const pageFromPath = extractPageFromPath(pathname);
    const pageFromQuery = Number(searchParams?.get("page"));
    if (Number.isFinite(pageFromQuery) && pageFromQuery > 0) {
      return pageFromQuery;
    }

    return pageFromPath;
  }, [pathname, searchParams]);

  // Get limit from URL parameters or use default
  const limit = useMemo(() => {
    const fromUrl = Number(searchParams?.get("limit"));
    return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : defaultLimit;
  }, [searchParams, defaultLimit]);

  // Handle page change navigation
  const setPage = useCallback(
    (newPage: number) => {
      if (newPage === currentPage) return;

      const newUrl = buildPageUrl(
        pathname,
        searchParams || new URLSearchParams(),
        newPage,
      );

      router.push(newUrl);
    },
    [router, pathname, searchParams, currentPage],
  );

  // Handle page size change navigation
  const setPageSize = useCallback(
    (nextSize: number) => {
      if (nextSize === limit) return;

      const { pathname: targetPath, query } = buildLimitUrl(
        basePath,
        searchParams || new URLSearchParams(),
        nextSize,
      );

      const queryString =
        Object.keys(query).length > 0
          ? "?" + new URLSearchParams(query).toString()
          : "";

      router.replace(targetPath + queryString, { scroll: false });
    },
    [router, basePath, searchParams, limit],
  );

  return {
    currentPage,
    limit,
    setPage,
    setPageSize,
  };
}
